import loginsAPI from '@/api/logins';

/**
 * Finds the client participants on a conversation that have no login yet, so the UI can
 * warn that they will not be able to see it.
 *
 * This is advisory only. Reading a client's logins needs `logins:read`, so for a role
 * without it every lookup used to 403 and raise a warning toast per participant — the
 * duplicate "Unable to check for clients without logins" errors QA hit. A user who
 * cannot read logins cannot act on the warning either, so the check is skipped rather
 * than attempted and reported.
 *
 * Uses Promise.allSettled so one participant's failed lookup doesn't discard the others.
 * `logins:read` does not cover a client outside the user's own groups — that needs
 * `logins:readAll` — so with read alone a single 403 was collapsing the whole batch and
 * the warning never appeared for anyone. A participant whose lookup fails is unknown
 * rather than login-less, so it is left out of the warning instead of suppressing it.
 */
export default {
  methods: {
    async getClientsWithoutLogins(participants) {
      if (!Array.isArray(participants) || participants.length === 0) {
        return [];
      }

      if (this.cannot('logins:read')) {
        return [];
      }

      const clientParticipants = participants.filter((p) => p.type === 'client');

      if (clientParticipants.length === 0) {
        return [];
      }

      const results = await Promise.allSettled(
        clientParticipants.map((participant) => loginsAPI.getAll(participant.uuid))
      );

      return clientParticipants.filter((participant, index) => {
        const result = results[index];

        // A rejected lookup tells us nothing about whether the client has a login, so
        // we stay quiet for that participant rather than warning about them.

        if (result.status !== 'fulfilled') {
          return false;
        }

        return result.value?.data?.data?.length === 0;
      });
    },
  },
};
